import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CallCenterModal from "./listing/CallCenterModal";
import { CallType } from "../../service/CallCenterApi";
import {
  CONDITION_OPERATORS,
  createEmptyConditionRow,
  parseTriggerConditionRows,
  PAYMENT_REMINDER_DEFAULT_ROWS,
  serializeTriggerCondition,
} from "./triggerCondition";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";

const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]";

export const EMPTY_CAMPAIGN_FORM = {
  name: "",
  callType: CallType.AI_AUTOMATED,
  active: true,
  scheduleCron: "0 0 9-17 * * MON-FRI",
  logic: "AND",
  limit: "50",
  retryMaxRetries: "2",
  retryDelayMinutes: "15",
};

export const buildCampaignInitialValues = (campaign) => {
  if (!campaign) {
    return { ...EMPTY_CAMPAIGN_FORM };
  }
  const parsed = parseTriggerConditionRows(campaign.triggerCondition);
  const retry = campaign.retryPolicy || {};
  return {
    name: campaign.name || "",
    callType: campaign.callType || CallType.AI_AUTOMATED,
    active: Boolean(campaign.active),
    scheduleCron: campaign.scheduleCron || "0 */10 * * * *",
    logic: parsed.logic || "AND",
    limit: parsed.limit === "" || parsed.limit == null ? "" : String(parsed.limit),
    retryMaxRetries: retry.maxRetries == null ? "" : String(retry.maxRetries),
    retryDelayMinutes: retry.retryDelayMinutes == null ? "" : String(retry.retryDelayMinutes),
  };
};

export const buildCampaignPayload = (values, rows) => ({
  name: values.name.trim(),
  callType: CallType.AI_AUTOMATED,
  active: Boolean(values.active),
  scheduleCron: values.scheduleCron.trim(),
  triggerCondition: serializeTriggerCondition({
    logic: values.logic,
    limit: values.limit,
    rows,
  }),
  retryPolicy: {
    maxRetries: values.retryMaxRetries === "" ? undefined : Number(values.retryMaxRetries),
    retryDelayMinutes:
      values.retryDelayMinutes === "" ? undefined : Number(values.retryDelayMinutes),
  },
});

const buildValidationSchema = (t) =>
  Yup.object({
    name: Yup.string().trim().required(t("ccCampaignNameRequired")),
    scheduleCron: Yup.string().trim().required(t("ccScheduleCronRequired")),
    active: Yup.boolean().required(),
    logic: Yup.string().oneOf(["AND", "OR"]),
    limit: Yup.number()
      .nullable()
      .transform((value, original) => (original === "" ? undefined : value))
      .min(1),
    retryMaxRetries: Yup.number()
      .nullable()
      .transform((value, original) => (original === "" ? undefined : value))
      .min(0),
    retryDelayMinutes: Yup.number()
      .nullable()
      .transform((value, original) => (original === "" ? undefined : value))
      .min(1),
  });

const CampaignFormModal = ({ open, campaign, saving, t, onClose, onSubmit }) => {
  const isEditing = Boolean(campaign?.id);
  const initialValues = useMemo(() => buildCampaignInitialValues(campaign), [campaign]);
  const parsedRows = useMemo(
    () => parseTriggerConditionRows(campaign?.triggerCondition).rows,
    [campaign]
  );
  const [rows, setRows] = useState([createEmptyConditionRow()]);

  useEffect(() => {
    if (open) {
      setRows(
        parsedRows.length && campaign?.id
          ? parsedRows
          : PAYMENT_REMINDER_DEFAULT_ROWS.map((row) => ({ ...row }))
      );
    }
  }, [open, parsedRows, campaign?.id]);

  const schema = useMemo(() => buildValidationSchema(t), [t]);

  const addRow = () => setRows((prev) => [...prev, createEmptyConditionRow()]);
  const removeRow = (index) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== index)));
  const updateRow = (index, patch) =>
    setRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, ...patch } : row)));

  const resetPaymentReminderDefaults = () => {
    setRows(PAYMENT_REMINDER_DEFAULT_ROWS.map((row) => ({ ...row })));
  };

  return (
    <CallCenterModal
      open={open}
      onClose={onClose}
      blockClose={saving}
      ariaLabelledBy="campaign-form-title"
      eyebrow={isEditing ? t("ccEditCampaign") : t("ccCreateNewCampaign")}
      title={isEditing ? t("ccEditCampaign") : t("ccCreateNewCampaign")}
      subtitle={t("ccCampaignPaymentReminderSubtitle")}
      maxWidth="3xl"
      closeLabel={t("ccClose")}
      contentVariant="none"
      bodyClassName="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6"
    >
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          enableReinitialize
          onSubmit={(values) => onSubmit(buildCampaignPayload(values, rows))}
        >
          {({ errors, touched, setFieldValue }) => (
            <Form>
              <h2 id="campaign-form-title" className="sr-only">
                {isEditing ? t("ccEditCampaign") : t("ccCreateNewCampaign")}
              </h2>
              <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/80 p-4 text-sm text-blue-900">
                <p className="font-semibold">{t("ccCampaignFlowTitle")}</p>
                <p className="mt-1 text-xs leading-5 text-blue-800">{t("ccCampaignFlowHint")}</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-blue-800">
                  <li>{t("ccCampaignFlowStep1")}</li>
                  <li>{t("ccCampaignFlowStep2")}</li>
                  <li>{t("ccCampaignFlowStep3")}</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={FILTER_LABEL_CLASS}>{t("ccCampaignName")}</label>
                  <Field
                    name="name"
                    placeholder={t("ccCampaignNamePlaceholder")}
                    className={`${FILTER_FIELD_CLASS} ${
                      touched.name && errors.name ? "border-red-500" : ""
                    }`}
                  />
                  {touched.name && errors.name ? (
                    <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                  ) : null}
                </div>

                <div>
                  <label className={FILTER_LABEL_CLASS}>{t("ccExecutionScheduleCron")}</label>
                  <Field
                    name="scheduleCron"
                    className={`${FILTER_FIELD_CLASS} font-mono text-xs ${
                      touched.scheduleCron && errors.scheduleCron ? "border-red-500" : ""
                    }`}
                  />
                  {touched.scheduleCron && errors.scheduleCron ? (
                    <p className="mt-1 text-xs text-red-600">{errors.scheduleCron}</p>
                  ) : (
                    <p className="mt-1 text-xs text-slate-400">{t("ccScheduleCronHint")}</p>
                  )}
                </div>

                <div>
                  <label className={FILTER_LABEL_CLASS}>{t("ccConditionLogic")}</label>
                  <Field as="select" name="logic" className={FILTER_FIELD_CLASS}>
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </Field>
                </div>

                <div>
                  <label className={FILTER_LABEL_CLASS}>{t("ccTicketLimit")}</label>
                  <Field
                    type="number"
                    name="limit"
                    min="1"
                    placeholder={t("ccTicketLimitPlaceholder")}
                    className={FILTER_FIELD_CLASS}
                  />
                </div>

                <div>
                  <label className={FILTER_LABEL_CLASS}>{t("ccMaxRetries")}</label>
                  <Field type="number" name="retryMaxRetries" min="0" className={FILTER_FIELD_CLASS} />
                </div>

                <div>
                  <label className={FILTER_LABEL_CLASS}>{t("ccDelayMin")}</label>
                  <Field type="number" name="retryDelayMinutes" min="1" className={FILTER_FIELD_CLASS} />
                </div>
              </div>

              <div className="mt-6">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">{t("ccTriggerConditions")}</h3>
                    <p className="mt-1 text-xs text-slate-500">{t("ccCampaignUnassignedHint")}</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        resetPaymentReminderDefaults();
                        setFieldValue("logic", "AND");
                        setFieldValue("limit", "50");
                      }}
                      className="text-xs font-semibold text-slate-600 hover:underline"
                    >
                      {t("ccCampaignResetDefaults")}
                    </button>
                    <button
                      type="button"
                      onClick={addRow}
                      className="text-xs font-semibold text-[#2B7FFF] hover:underline"
                    >
                      {t("ccAddRule")}
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {rows.map((row, index) => (
                    <div
                      key={index}
                      className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-3"
                    >
                      <select
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700"
                        value={row.mode}
                        onChange={(event) => updateRow(index, { mode: event.target.value })}
                      >
                        <option value="object">{t("ccFieldMode")}</option>
                        <option value="string">{t("ccExprMode")}</option>
                      </select>
                      {row.mode === "string" ? (
                        <input
                          className={`${FILTER_FIELD_CLASS} min-w-[200px] flex-1`}
                          placeholder={t("ccExpressionPlaceholder")}
                          value={row.expression}
                          onChange={(event) => updateRow(index, { expression: event.target.value })}
                        />
                      ) : (
                        <>
                          <input
                            placeholder={t("ccFieldName")}
                            className={`${FILTER_FIELD_CLASS} min-w-[120px] flex-1`}
                            value={row.field}
                            onChange={(event) => updateRow(index, { field: event.target.value })}
                          />
                          <select
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs"
                            value={row.operator}
                            onChange={(event) => updateRow(index, { operator: event.target.value })}
                          >
                            {CONDITION_OPERATORS.map((op) => (
                              <option key={op} value={op}>
                                {op}
                              </option>
                            ))}
                          </select>
                          <input
                            placeholder={t("ccValue")}
                            className={`${FILTER_FIELD_CLASS} min-w-[120px] flex-1`}
                            value={row.value}
                            onChange={(event) => updateRow(index, { value: event.target.value })}
                            disabled={["ISNULL", "ISNOTNULL"].includes(row.operator)}
                          />
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="rounded-lg px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                      >
                        {t("ccRemove")}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-5">
                <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                  <Field type="checkbox" name="active" className={CHECKBOX_CLASS} />
                  {t("ccSetAsActive")}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-10 items-center rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {t("ccCancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={`${LISTING_PRIMARY_BUTTON_CLASS} disabled:opacity-50`}
                  >
                    {saving ? t("ccSaving") : isEditing ? t("ccSaveChanges") : t("ccCreateCampaign")}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
    </CallCenterModal>
  );
};

export default CampaignFormModal;
